import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  User,
  Building2,
  Mail,
  Phone,
  Globe,
  MapPin,
  Tag,
  ArrowDownWideNarrow,
  RefreshCw,
  Calendar,
  DollarSign,
  FileText,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import WorkspaceLayout from "@/components/layout/WorkspaceLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CLIENT_TYPES = ["Retail", "Business", "Promo"];

const EMPTY_CUSTOMER = {
  business_name: "",
  contact_name: "",
  email: "",
  phone: "",
  website: "",
  physical_address: "",
  postal_address: "",
  notes: "",
  tags: [],
  default_client_type: "Business",
  default_margin_percent: null,
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const formatMoney = (value) => {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "Never";
  try {
    return new Date(dateStr).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

export default function Customers() {
  const { isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [tagFilter, setTagFilter] = useState("all");
  const [allTags, setAllTags] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState(EMPTY_CUSTOMER);
  const [tagsInput, setTagsInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  
  // Jobs modal state
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [isJobsModalOpen, setIsJobsModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState("default");

  useEffect(() => {
    loadCustomers(search);
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFilter, sortBy]);

  const loadCustomers = async (searchQuery = "") => {
    setIsLoading(true);
    try {
      const params = { search: searchQuery, limit: 100 };
      if (tagFilter && tagFilter !== "all") {
        params.tag = tagFilter;
      }
      if (sortBy === "spend_desc") {
        params.sort = "spend_desc";
      }
      const res = await axios.get(`${API}/customers`, { params });
      setCustomers(res.data.customers || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to load customers", error);
      toast.error("Failed to load customers");
    } finally {
      setIsLoading(false);
    }
  };

  const loadTags = async () => {
    try {
      const res = await axios.get(`${API}/customers/tags`);
      setAllTags(res.data.tags || []);
    } catch (error) {
      console.error("Failed to load tags", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadCustomers(search);
  };

  const openAddModal = () => {
    setEditingCustomer(null);
    setFormData(EMPTY_CUSTOMER);
    setTagsInput("");
    setIsModalOpen(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      business_name: customer.business_name || "",
      contact_name: customer.contact_name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      website: customer.website || "",
      physical_address: customer.physical_address || "",
      postal_address: customer.postal_address || "",
      notes: customer.notes || "",
      tags: customer.tags || [],
      default_client_type: customer.default_client_type || "Business",
      default_margin_percent: customer.default_margin_percent || null,
    });
    setTagsInput((customer.tags || []).join(", "));
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.business_name && !formData.contact_name) {
      toast.error("Business name or contact name is required");
      return;
    }
    
    setIsSaving(true);
    try {
      const tags = tagsInput
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t);
      
      const payload = { ...formData, tags };
      
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, payload);
        toast.success("Customer updated");
      } else {
        await axios.post(`${API}/customers`, payload);
        toast.success("Customer created");
      }
      
      setIsModalOpen(false);
      loadCustomers(search);
      loadTags();
    } catch (error) {
      console.error("Failed to save customer", error);
      toast.error("Failed to save customer");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm("Delete this customer? This cannot be undone.")) return;
    
    try {
      await axios.delete(`${API}/customers/${customerId}`);
      toast.success("Customer deleted");
      loadCustomers(search);
    } catch (error) {
      console.error("Failed to delete customer", error);
      toast.error("Failed to delete customer");
    }
  };

  const openJobsModal = async (customer) => {
    setSelectedCustomer(customer);
    setIsJobsModalOpen(true);
    
    try {
      const res = await axios.get(`${API}/customers/${customer.id}/jobs`);
      setCustomerJobs(res.data.jobs || []);
    } catch (error) {
      console.error("Failed to load customer jobs", error);
      setCustomerJobs([]);
    }
  };

  return (
    <WorkspaceLayout
      title="Customers"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={openAddModal}
            data-testid="add-customer-btn"
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Customer
          </button>
        </div>
      }
    >
      {/* Search and Filters Bar */}
      <div className="bg-white border-b border-workspace-border px-6 py-3">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-workspace-text-muted" />
            <input
              type="text"
              placeholder="Search by business name, contact, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-workspace-bg rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/30 border border-workspace-border"
              data-testid="customer-search-input"
            />
          </div>
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger className="w-40 bg-white rounded-md border-workspace-border" data-testid="tag-filter">
              <Tag className="w-4 h-4 mr-2 text-workspace-text-muted" />
              <SelectValue placeholder="All Tags" />
            </SelectTrigger>
            <SelectContent className="rounded-md bg-white border border-workspace-border">
              <SelectItem value="all">All Tags</SelectItem>
              {allTags.map((tag) => (
                <SelectItem key={tag} value={tag}>
                  {tag}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48 bg-white rounded-md border-workspace-border" data-testid="sort-filter">
              <ArrowDownWideNarrow className="w-4 h-4 mr-2 text-workspace-text-muted" />
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="rounded-md bg-white border border-workspace-border">
              <SelectItem value="default" data-testid="sort-default">Sort: Name</SelectItem>
              <SelectItem value="spend_desc" data-testid="sort-spend-desc">Highest Spend</SelectItem>
            </SelectContent>
          </Select>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition-colors disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            Search
          </button>
          <div className="ml-auto text-sm text-workspace-text-secondary">
            {total} customer{total !== 1 ? "s" : ""}
          </div>
        </form>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {/* Customer List */}
        {isLoading ? (
          <div className="text-center py-12 text-workspace-text-muted">
            <RefreshCw className="w-8 h-8 mx-auto mb-3 animate-spin opacity-50" />
            <p className="text-sm">Loading customers...</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12">
            <User className="w-12 h-12 mx-auto mb-4 text-workspace-text-muted opacity-50" />
            <h3 className="text-lg font-medium text-workspace-text-primary mb-2">No customers found</h3>
            <p className="text-sm text-workspace-text-muted mb-4">
              {search ? "Try a different search term" : "Add your first customer to get started"}
            </p>
            <button
              onClick={openAddModal}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-aa-md bg-aa-primary text-white text-aa-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Add Customer
            </button>
          </div>
        ) : (
          <div className="grid gap-4">
            {customers.map((customer) => (
              <div
                key={customer.id}
                className="bg-white rounded-lg border border-workspace-border shadow-sm p-4 hover:shadow-md transition-all"
                data-testid={`customer-card-${customer.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-workspace-text-primary">
                        {customer.business_name || customer.contact_name}
                      </h3>
                      <Badge variant="outline" className="text-xs">
                        {customer.default_client_type}
                      </Badge>
                      {customer.tags?.map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-workspace-text-secondary">
                      {customer.contact_name && customer.business_name && (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-workspace-text-muted" />
                          <span>{customer.contact_name}</span>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-workspace-text-muted" />
                          <a href={`mailto:${customer.email}`} className="hover:text-orange-500">
                            {customer.email}
                          </a>
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4 text-workspace-text-muted" />
                          <a href={`tel:${customer.phone}`} className="hover:text-orange-500">
                            {customer.phone}
                          </a>
                        </div>
                      )}
                      {customer.website && (
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-workspace-text-muted" />
                          <a href={customer.website} target="_blank" rel="noopener noreferrer" className="hover:text-orange-500 flex items-center gap-1">
                            Website <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-6 mt-3 text-xs text-workspace-text-muted">
                      <div className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        <span>{customer.job_count || 0} job{(customer.job_count || 0) !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3.5 h-3.5" />
                        <span>{formatCurrency(customer.total_revenue)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>Last order: {formatDate(customer.last_order_date)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {customer.job_count > 0 && (
                      <button
                        onClick={() => openJobsModal(customer)}
                        className="p-2 text-workspace-text-muted hover:text-orange-500 hover:bg-workspace-bg rounded transition-colors"
                        title="View Job History"
                        data-testid={`view-job-history-${customer.id}`}
                      >
                        <FileText className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => openEditModal(customer)}
                      className="p-2 text-workspace-text-muted hover:text-orange-500 hover:bg-workspace-bg rounded transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(customer.id)}
                        className="p-2 text-workspace-text-muted hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Customer Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? "Edit Customer" : "Add Customer"}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer 
                ? "Update customer details" 
                : "Add a new customer for quick selection in quotes"}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name</Label>
                <Input
                  id="business_name"
                  value={formData.business_name}
                  onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                  placeholder="Company Pty Ltd"
                  data-testid="customer-business-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  placeholder="John Smith"
                  data-testid="customer-contact-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@company.com"
                  data-testid="customer-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="0400 000 000"
                  data-testid="customer-phone"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://www.company.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="physical_address">Physical Address</Label>
              <Input
                id="physical_address"
                value={formData.physical_address}
                onChange={(e) => setFormData({ ...formData, physical_address: e.target.value })}
                placeholder="123 Main St, Sydney NSW 2000"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="postal_address">Postal Address</Label>
              <Input
                id="postal_address"
                value={formData.postal_address}
                onChange={(e) => setFormData({ ...formData, postal_address: e.target.value })}
                placeholder="PO Box 123, Sydney NSW 2000"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="default_client_type">Default Client Type</Label>
                <Select
                  value={formData.default_client_type}
                  onValueChange={(v) => setFormData({ ...formData, default_client_type: v })}
                >
                  <SelectTrigger data-testid="customer-client-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CLIENT_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="default_margin">Default Margin % (optional)</Label>
                <Input
                  id="default_margin"
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={formData.default_margin_percent || ""}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    default_margin_percent: e.target.value ? parseFloat(e.target.value) : null 
                  })}
                  placeholder="Use default"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="VIP, Corporate, Repeat"
                data-testid="customer-tags"
              />
              <p className="text-xs text-gray-400">Use tags to categorize customers</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this customer..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} data-testid="save-customer-btn">
              {isSaving ? "Saving..." : editingCustomer ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Job History Modal */}
      <Dialog open={isJobsModalOpen} onOpenChange={setIsJobsModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle data-testid="customer-profile-title">
              {selectedCustomer?.business_name || selectedCustomer?.contact_name || "Customer"}
            </DialogTitle>
            <DialogDescription>Job History</DialogDescription>
          </DialogHeader>

          <div className="max-h-[60vh] overflow-y-auto" data-testid="customer-job-history">
            {customerJobs.length === 0 ? (
              <p className="text-center text-gray-500 py-8" data-testid="customer-job-history-empty">
                No jobs found
              </p>
            ) : (
              <div className="space-y-2">
                {customerJobs.map((job) => (
                  <Link
                    key={job.id}
                    to={`/quote?job=${job.id}`}
                    className="block p-3 bg-workspace-bg rounded-md hover:bg-workspace-hover transition-colors"
                    data-testid={`customer-job-row-${job.id}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-workspace-text-primary truncate">
                          {job.job_name || "Untitled"}
                        </div>
                        <div className="text-xs text-workspace-text-muted">
                          {formatDate(job.quote_date || job.created_at)} • {job.status}
                        </div>
                      </div>
                      <div
                        className="font-semibold text-workspace-text-primary whitespace-nowrap"
                        data-testid={`customer-job-total-${job.id}`}
                      >
                        {job.total_inc_gst != null ? formatMoney(job.total_inc_gst) : "—"}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </WorkspaceLayout>
  );
}
